import { useMasterData } from 'master-data/hooks/useMasterData/useMasterData'
import { useSetSession } from 'session/src/hooks/useSetSession'
import { type CartDispatch, useCartDispatch } from '../context/CartProvider'
import { openLimitWarningModal } from '../context/LimitsWarning'
import { useAnalytics } from '../hooks/useAnalytics'
import type { PurchaseType } from '../services/graphql.types'
import { getPath } from '../utils/exceptions'
import {
	type GraphQlError,
	getErrorHandler,
	useHandleErrors,
} from './useHandleErrors'
import { useNotifications } from './useNotifications'

export interface ErrorItem {
	message: string
	path?: string[]
	extensions?: Extensions
}

interface Extensions {
	exception?: Exception
	oneItemShoppingCartOnly?: boolean
	shoppingCart?: PurchaseType | null
}

interface Exception {
	path: string[]
	message: string
}

interface UseErrorManagement {
	addErrorManagement: (
		errors: ErrorItem | ErrorItem[],
		query: string
	) => Promise<void>
}

interface AddErrorHandlersType {
	error: ErrorItem
	dispatch: CartDispatch
	environment?: string
}

/**
 * Hook that handles the errors of the shopping cart.
 */
export const useErrorManagement = (): UseErrorManagement => {
	const dispatch = useCartDispatch()
	const {
		itemWithoutStockNotification: itemWithoutStockNotificationFn,
		unexpectedErrorNotification,
	} = useNotifications()
	const { refreshSession } = useSetSession()
	const { sendBagErrorAddItemEvent } = useAnalytics()
	const { handleError } = useHandleErrors()
	const { environment } = useMasterData()

	const addErrorHandlers = (
		itemWithoutStockNotification: () => void,
		query: string,
		errors: ErrorItem | ErrorItem[]
	): {
		[key: string]: (param: AddErrorHandlersType) => void | Promise<void>
	} => ({
		MaxNumberOfItems: ({ dispatch: cartDispatch }: AddErrorHandlersType) => {
			openLimitWarningModal(cartDispatch)
		},
		AmountLimit: ({ dispatch: cartDispatch }: AddErrorHandlersType) => {
			openLimitWarningModal(cartDispatch)
		},
		ItemWithoutStock: () => {
			itemWithoutStockNotification()
		},
		MalformedToken: async () => {
			try {
				await refreshSession()
			} catch (_e) {
				handleError(getErrorHandler(query), errors as GraphQlError[])
			}
		},
		TokenExpired: async () => {
			try {
				await refreshSession()
			} catch (_e) {
				handleError(getErrorHandler(query), errors as GraphQlError[])
			}
		},
	})

	const addErrorManagement = async (
		errors: ErrorItem | ErrorItem[],
		query: string
	): Promise<void> => {
		const errorHandlers = addErrorHandlers(
			itemWithoutStockNotificationFn,
			query,
			errors
		)
		sendBagErrorAddItemEvent()

		if (!Array.isArray(errors)) {
			unexpectedErrorNotification()
			return
		}

		await Promise.all(
			errors.map(async (error) => {
				const type = Object.keys(errorHandlers).find((errorKey) =>
					getPath(error).includes(errorKey)
				)
				const handler = type ? errorHandlers[type] : null

				if (handler) {
					await handler({ error, dispatch, environment })
				} else {
					unexpectedErrorNotification()
				}
			})
		)
	}

	return { addErrorManagement }
}
